/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Select } from "antd";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import Divider from "../../components/Common/Divider";
import ToggleButtonWithInfo from "../../components/Common/ToggleButtonWithInfo";
import FormSelect from "../../components/Common/FormSelect";
import FormControl from "../../components/Common/FormControl";
import FormLabel from "../../components/Common/FormLabel";
import FormCheck from "react-bootstrap/FormCheck";
import DatePicker from "react-datepicker";
import {
  fetchCoachSettings,
  updateCoachSettings,
} from "../../redux/Slices/coachSettingsSlice";
import Loader from "../../components/Common/Loader";
import ToastNotification from "../../components/Common/ToastNotification";
import { isValidDecimalInput } from "../../utils/Utils";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt } from "react-icons/fa";
import useFetchCoachProfile from "../../utils/useFetchCoachProfile";
import { UnitContext } from "../../hooks/UnitProvider";
import { getSetupStripeForCoachThunk } from "../../redux/Slices/leadOfferSlice";
import appInsights from "../../AppInsights";

const { Option } = Select;

const Settings = () => {
  const dispatch = useDispatch();
  const { trans } = useCustomTranslation();
  useFetchCoachProfile();
  const { toggleUnit } = useContext(UnitContext);

  const { profileData } = useSelector((state) => state.profile);

  const {
    settings,
    loading,
    error: updateError,
    success: updateSuccess,
  } = useSelector((state) => state.coachSettings);

  const [formData, setFormData] = useState({});
  const [toastConfig, setToastConfig] = useState(null);
  const [errors, setErrors] = useState({});
  const [selectedDays, setSelectedDays] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isStripeConnected, setIsStripeConnected] = useState(
    localStorage.getItem("isStripeConnected") === true ||
      localStorage.getItem("isStripeConnected") === "true"
  );

  const pageLoadTime = useRef(Date.now());
  const lastSaveTime = useRef(Date.now());

  const coachId = profileData?.coachId;

  useEffect(() => {
    const openTime = new Date().toLocaleString();
    appInsights.trackPageView({
      name: "Settings Page",
      properties: {
        openedAt: openTime,
        coachId: coachId,
      },
    });
  }, [coachId]);

  useEffect(() => {
    if (coachId) {
      localStorage.setItem("isStripeConnected", false);
      dispatch(fetchCoachSettings({ coachId }));
    }
  }, [coachId]);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "isStripeConnected") {
        setIsStripeConnected(e.newValue === "true");
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Cleanup
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [isStripeConnected]);

  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  useEffect(() => {
    const days = formData?.sendCheckinReminderEvery?.on;

    // accept only valid numbers
    const selectedDays =
      formData?.sendCheckinReminderEvery?.occur === "month" && days
        ? days
            .split(",")
            .map((d) => parseInt(d, 10))
            .filter((d) => !isNaN(d))
        : [];

    setSelectedDays(selectedDays);
  }, [formData]);

  useEffect(() => {
    if (formData?.measurementSystem) {
      toggleUnit(
        formData?.measurementSystem ? formData?.measurementSystem : "metric"
      );
    }
  }, [formData.measurementSystem]);

  // 🔹 Track page unload
  useEffect(() => {
    return () => {
      const totalTimeSec = ((Date.now() - pageLoadTime.current) / 1000).toFixed(
        2,
      );
      appInsights.trackEvent({
        name: "Settings_Page_Unload",
        properties: {
          coachId: coachId,
          totalTimeSpent: `${totalTimeSec}s`,
        },
      });
    };
  }, [coachId]);

  const measurementUnits = [
    { value: "metric", label: "Metric" },
    { value: "imperial", label: "Imperial" },
  ];

  // Handle toggle changes
  const handleToggleChange = (event, fieldName) => {
    const { checked } = event.target;

    // 🔹 Track toggle changes
    appInsights.trackEvent({
      name: "Settings_Toggle_Changed",
      properties: {
        fieldName: fieldName,
        newValue: checked,
        coachId: coachId,
      },
    });

    if (fieldName === "checkIns") {
      setFormData((prevState) => ({
        ...prevState,
        checkIns: checked,
      }));
      if (!checked) {
        setFormData((prevState) => ({
          ...prevState,
          sendRemindersToClient: checked,
        }));
      }
    } else if (fieldName === "sendRemindersToClient") {
      setFormData((prevState) => ({
        ...prevState,
        sendRemindersToClient: checked,
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        clientHandling: {
          ...prevState.clientHandling,
          [fieldName]: checked,
        },
      }));
    }
  };

  // Handle email notification toggle changes
  const handleEmailNotificationToggleChange = (event, fieldName) => {
    const { checked } = event.target;

    // 🔹 Track email notification toggle changes
    appInsights.trackEvent({
      name: "Settings_Email_Notification_Changed",
      properties: {
        fieldName: fieldName,
        newValue: checked,
        coachId: coachId,
      },
    });

    setFormData((prevState) => ({
      ...prevState,
      emailNotification: {
        ...prevState.emailNotification,
        [fieldName]: checked,
      },
    }));
  };

  // Handle form data changes
  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "measurementSystem") {
      // 🔹 Track measurement system change
      appInsights.trackEvent({
        name: "Settings_Measurement_System_Changed",
        properties: {
          previousValue: formData?.measurementSystem,
          newValue: value,
          coachId: coachId,
        },
      });
      toggleUnit(value);
    }

    // Check if the value is for oldChatsThreshold and validate it
    if (
      name === "oldChatsThreshold" &&
      (!isValidDecimalInput(value) || value === "0")
    ) {
      return;
    }
    if (
      name === "followUpThreshold" &&
      (!isValidDecimalInput(value) || value === "0")
    ) {
      return;
    }

    // Check if we're changing a nested property in sendCheckinReminderEvery
    if (name in formData?.sendCheckinReminderEvery) {
      setFormData((prevState) => ({
        ...prevState,
        sendCheckinReminderEvery: {
          ...prevState.sendCheckinReminderEvery,
          [name]: value,
        },
      }));
    } else if (name in formData?.mealPlanSetting) {
      // Handle changes for mealPlanSetting
      setFormData((prevState) => ({
        ...prevState,
        mealPlanSetting: {
          ...prevState.mealPlanSetting,
          [name]: value,
        },
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const handleSelectChange = (value) => {
    // 🔹 Track reminder occurrence change
    appInsights.trackEvent({
      name: "Settings_Reminder_Occurrence_Changed",
      properties: {
        previousValue: formData?.sendCheckinReminderEvery?.occur,
        newValue: value,
        coachId: coachId,
      },
    });

    setFormData((prevState) => ({
      ...prevState,
      sendCheckinReminderEvery: {
        ...prevState.sendCheckinReminderEvery,
        on: "",
      },
    }));

    setFormData((prevState) => ({
      ...prevState,
      sendCheckinReminderEvery: {
        ...prevState.sendCheckinReminderEvery,
        occur: value,
      },
    }));
  };

  // Handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();

    const newErrors = {};

    const oldChatsThreshold = parseFloat(formData.oldChatsThreshold);
    if (
      isNaN(oldChatsThreshold) ||
      oldChatsThreshold < 1 ||
      oldChatsThreshold > 365
    ) {
      newErrors.oldChatsThreshold = trans("Settings.oldChatsThresholdError");
    }

    const followUpThreshold = parseFloat(formData.followUpThreshold);
    if (
      isNaN(followUpThreshold) ||
      followUpThreshold < 1 ||
      followUpThreshold > 365
    ) {
      newErrors.followUpThreshold = trans("Settings.oldChatsThresholdError");
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // 🔹 Track validation errors
      appInsights.trackException({
        exception: new Error("Settings_Validation_Failed"),
        properties: {
          errors: Object.keys(newErrors),
          coachId: coachId,
        },
      });

      return;
    }

    let selectedCalendarDays;
    if (selectedDays.length > 0) {
      selectedCalendarDays = selectedDays.join(",");
    }

    const requestPayload = {
      coachId: coachId,
      emailNotificationsMessage:
        formData?.emailNotification.clientSendYouMessage,
      emailNotificationsProgress:
        formData.emailNotification.clientUpdatedTheirProgress,
      emailNotificationsNewLead: formData.emailNotification.youHaveNewLead,
      measurementSystem: formData?.measurementSystem || "",
      clientListFilterThreshold: formData?.oldChatsThreshold || 15,
      ClientFollowUpThreshold: formData?.followUpThreshold || 30,
      defaultMealPlanType: formData?.mealPlanSetting?.defaultMealPlan,
      displayRecipeIngredients:
        formData?.mealPlanSetting?.displayRecipeInGredientIn,
      clientBirthdayReminder:
        formData?.clientHandling?.automaticReminderBirthday,
      sendCheckinReminders: formData?.sendRemindersToClient,
      checkinReminderUnit: formData?.sendCheckinReminderEvery.unit,
      checkinReminderOccur: formData?.sendCheckinReminderEvery.occur,
      checkinReminderOn:
        formData?.sendCheckinReminderEvery.occur === "month"
          ? selectedCalendarDays
          : formData?.sendCheckinReminderEvery.on,
      pushNotificationReminder: formData?.remindClientVia.pushNotification,
      chatMessageReminder: formData?.remindClientVia.chatMessages,
      emailReminder: formData?.remindClientVia?.email,
    };

    // 🔹 Track settings save attempt
    appInsights.trackEvent({
      name: "Settings_Save_Attempt",
      properties: {
        coachId: coachId,
        payload: requestPayload,
      },
    });

    lastSaveTime.current = Date.now();
    setToastConfig(null);
    dispatch(updateCoachSettings({ coachId, requestPayload }));
  };

  const handleWeekDaysChange = (event) => {
    // Track week days selection
    appInsights.trackEvent({
      name: "Settings_WeekDays_Changed",
      properties: {
        selectedDays: event,
        coachId: coachId,
      },
    });

    const selectedDay = event;
    setFormData((prevState) => ({
      ...prevState,
      sendCheckinReminderEvery: {
        ...prevState.sendCheckinReminderEvery,
        on: selectedDay.toString(),
      },
    }));
  };

  const weekDays = [
    { value: "monday", label: trans("Common.monday") },
    { value: "tuesday", label: trans("Common.tuesday") },
    { value: "wednesday", label: trans("Common.wednesday") },
    { value: "thursday", label: trans("Common.thursday") },
    { value: "friday", label: trans("Common.friday") },
    { value: "saturday", label: trans("Common.saturday") },
    { value: "sunday", label: trans("Common.sunday") },
  ];
  const Occur = [
    { value: "week", label: trans("Common.week") },
    { value: "month", label: trans("Common.month") },
  ];

  // Handle checkbox changes for reminder methods
  const handleReminderMethodChange = (event) => {
    const { name, checked } = event.target;

    // 🔹 Track reminder method changes
    appInsights.trackEvent({
      name: "Settings_Reminder_Method_Changed",
      properties: {
        method: name,
        enabled: checked,
        coachId: coachId,
      },
    });

    setFormData((prevState) => ({
      ...prevState,
      remindClientVia: {
        ...prevState.remindClientVia,
        [name]: checked,
      },
    }));
  };
  // Get the number of days in a month
  const getDaysInMonth = (date) => {
    const month = date.getMonth();
    const year = date.getFullYear();
    return new Date(year, month + 1, 0).getDate();
  };

  // Handle date selection and deselection based on day (1-31)
  const handleDateChange = (date) => {
    const day = date.getDate();

    // 🔹 Track calendar date selection
    appInsights.trackEvent({
      name: "Settings_Calendar_Date_Selected",
      properties: {
        selectedDay: day,
        coachId: coachId,
      },
    });

    // Add or remove day from selectedDays without duplicates
    setSelectedDays((prevDays) => {
      let updatedDays;

      if (prevDays.includes(day)) {
        updatedDays = prevDays.filter((d) => d !== day);
      } else {
        updatedDays = [...new Set([...prevDays, day])];
      }

      return updatedDays;
    });
  };

  // Handle when the user changes the month
  const handleMonthChange = (date) => {
    setCurrentMonth(date);
  };

  // Toggle calendar visibility
  const toggleCalendar = () => {
    // 🔹 Track calendar toggle
    appInsights.trackEvent({
      name: "Settings_Calendar_Toggled",
      properties: {
        action: calendarOpen ? "close" : "open",
        coachId: coachId,
      },
    });

    setCalendarOpen((prev) => !prev);
  };

  // Adjust selected days if they are invalid for the current month
  const adjustForInvalidDays = (monthDate) => {
    const daysInMonth = getDaysInMonth(monthDate);
    let adjustedDays = [...selectedDays];

    adjustedDays = adjustedDays.map((day) => {
      if (day > daysInMonth) {
        if (monthDate.getMonth() === 1) {
          if (day === 30) return 27;
          if (day === 31) return 28;
        } else if (day > daysInMonth) {
          return daysInMonth - (selectedDays.indexOf(day) - 1);
        }
        return daysInMonth;
      }
      return day;
    });

    return [...new Set(adjustedDays)];
  };

  // Format the selected days for display in the input field
  const formatSelectedDays = () => {
    const adjustedSelectedDays = adjustForInvalidDays(currentMonth);
    if (adjustedSelectedDays?.length === 0) return "Select dates";
    return adjustedSelectedDays?.sort((a, b) => a - b).join(", ");
  };

  // Toast notification logic
  useEffect(() => {
    if (!toastConfig) {
      if (updateSuccess) {
        // Track successful settings update
        const saveTimeSec = (
          (Date.now() - lastSaveTime.current) /
          1000
        ).toFixed(2);
        appInsights.trackEvent({
          name: "Settings_Update_Success",
          properties: {
            coachId: coachId,
            saveTime: `${saveTimeSec}s`,
          },
        });

        setToastConfig({
          type: "success",
          message: updateSuccess,
          heading: trans("Common.success"),
        });
        setTimeout(() => {
          setToastConfig(null);
        }, 3000);
      } else if (updateError) {
        // Track settings update failure
        appInsights.trackException({
          exception: updateError,
          properties: {
            name: "Settings_Update_Failed",
            coachId: coachId,
          },
        });

        setToastConfig({
          type: "error",
          message: updateError,
          heading: trans("Common.failed"),
        });
        setTimeout(() => {
          setToastConfig(null);
        }, 3000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSuccess, trans, updateError]);

  const handleReconnect = () => {
    // Track Stripe reconnect attempt
    appInsights.trackEvent({
      name: "Stripe_Reconnect_Attempt",
      properties: {
        coachId: coachId,
      },
    });

    dispatch(getSetupStripeForCoachThunk({ coachId })).then((response) => {
      const { id, url } = response?.payload;

      localStorage.setItem("checkoutSessionId", id);
      localStorage.setItem("isCoach", true);
      localStorage.setItem("isAdminCoach", false);
      localStorage.setItem("isStripeConnected", false);

      // Track Stripe redirect
      appInsights.trackEvent({
        name: "Stripe_Redirect",
        properties: {
          coachId: coachId,
          checkoutSessionId: id,
        },
      });

      url && window.open(url, "_blank");
      // window.location.href = url;
    });
  };
  return (
    <div className=" formContainer">
      {loading ? (
        <Loader />
      ) : (
        <Form className="templateWrapper px-4 py-4 m-3" onSubmit={handleSubmit}>
          <Row className="pb-4">
            <Col>
              <h4 className="pb-2 text-color">{trans("Common.settings")}</h4>
            </Col>
            <Col className="d-flex justify-content-end">
              <Button className="saveButton" type="submit">
                <i className="bi bi-floppy2-fill mr-2"></i>
                {trans("Common.save")}
              </Button>
            </Col>
          </Row>

          <Row>
            <h5>
              {trans("Common.email") + " " + trans("Common.notifications")}
            </h5>
          </Row>

          <Row>
            <ToggleButtonWithInfo
              id="clientSendYouMessage"
              label={trans("Settings.clientMessage")}
              checked={formData?.emailNotification?.clientSendYouMessage}
              onChange={(e) =>
                handleEmailNotificationToggleChange(e, "clientSendYouMessage")
              }
              className="mt-1"
            />
            <ToggleButtonWithInfo
              id="clientUpdatedTheirProgress"
              label={trans("Settings.clientProgress")}
              checked={formData?.emailNotification?.clientUpdatedTheirProgress}
              onChange={(e) =>
                handleEmailNotificationToggleChange(
                  e,
                  "clientUpdatedTheirProgress"
                )
              }
              className="mt-1"
            />
            <ToggleButtonWithInfo
              id="youHaveNewLead"
              label={trans("Settings.newLead")}
              checked={formData.emailNotification?.youHaveNewLead}
              onChange={(e) =>
                handleEmailNotificationToggleChange(e, "youHaveNewLead")
              }
              className="mt-1"
            />
          </Row>

          <Divider className="mt-3" />

          <Row className="mt-3">
            <h5>
              {trans("Common.measurements") + " " + trans("Common.system")}
            </h5>
          </Row>
          <Row>
            <div className="pr-5 pl-5">
              <FormSelect
                label=""
                name="measurementSystem"
                value={formData?.measurementSystem || "metric"}
                onChange={handleChange}
                options={measurementUnits}
              />
            </div>
          </Row>

          <Divider className="mt-4" />

          <Row className="mt-4">
            <h5 className="mr-2 w-auto">{trans("Settings.stripeConnect")}</h5>
            <div
              className={`is-connect-circle ${
                formData.isStripeConnected || isStripeConnected
                  ? "green"
                  : "red"
              }`}
            ></div>
            <span className="w-auto mt-1">
              {formData.isStripeConnected || isStripeConnected
                ? trans("Settings.connected")
                : trans("Settings.disconnected")}
            </span>
            {!formData.isStripeConnected && !isStripeConnected && (
              <Button
                className="saveButton ml-3 w-auto"
                onClick={handleReconnect}
              >
                {trans("Reconnect")}
              </Button>
            )}
          </Row>
          <Divider className="mt-3" />

          <Row className="mt-4">
            <h5 className="mr-2">{trans("Settings.clientList")}</h5>
            <Col xs={6} md={6} sm={6} className="d-flex align-items-center">
              <FormLabel
                label={trans("Settings.oldChatsDaysFilterThreshould")}
              />
              <OverlayTrigger
                placement="top-start"
                overlay={
                  <Tooltip>
                    {trans("Settings.oldChatsDaysFilterThreshouldTextInfo")}
                  </Tooltip>
                }
              >
                <span className="cursor-pointer">
                  <i className="bi bi-info-circle" />
                </span>
              </OverlayTrigger>

              <div className="flex-direction-column">
                <FormControl
                  type="text"
                  placeholder={
                    trans("Common.enter") + " " + trans("Settings.days")
                  }
                  name="oldChatsThreshold"
                  value={formData.oldChatsThreshold}
                  onChange={handleChange}
                  isInvalid={!!errors.oldChatsThreshold}
                  feedback={errors.oldChatsThreshold}
                  className="ms-2"
                />
              </div>
            </Col>

            {/* Follow-ups Threshold */}
            <Col xs={6} md={6} sm={6} className="d-flex align-items-center">
              <FormLabel
                label={trans("Settings.followUpsDaysFilterThreshould")}
              />
              <OverlayTrigger
                placement="top-start"
                overlay={
                  <Tooltip>
                    {trans("Settings.followUpsDaysFilterThreshouldTextInfo")}
                  </Tooltip>
                }
              >
                <span className="cursor-pointer">
                  <i className="bi bi-info-circle" />
                </span>
              </OverlayTrigger>
              <div className="flex-direction-column">
                <FormControl
                  type="text"
                  placeholder={
                    trans("Common.enter") + " " + trans("Settings.days")
                  }
                  name="followUpThreshold"
                  value={formData.followUpThreshold}
                  onChange={handleChange}
                  isInvalid={!!errors.followUpThreshold}
                  feedback={errors.followUpThreshold}
                  className="ms-2"
                />
              </div>
            </Col>
          </Row>

          <Divider className="mt-3" />

          {/* New Toggle Buttons with info */}
          <Row className="mt-4">
            <h5>{trans("Settings.clientHandling")}</h5>
          </Row>

          <Row>
            <ToggleButtonWithInfo
              id="automaticReminderBirthday"
              label={trans("Settings.automaticReminderBirthdayLabel")}
              checked={formData?.clientHandling?.automaticReminderBirthday}
              onChange={(e) =>
                handleToggleChange(e, "automaticReminderBirthday")
              }
              className="mt-1"
              info={trans("Settings.automaticReminderBirthdayInfoText")}
            />
          </Row>
          {/* <Row>
            <ToggleButtonWithInfo
              id="autoRevokeClientMobileAppAccess"
              label={trans("Settings.AutoRevokeClientMobileAppAccessLabel")}
              checked={
                formData?.clientHandling?.autoRevokeClientMobileAppAccess
              }
              onChange={(e) =>
                handleToggleChange(e, "autoRevokeClientMobileAppAccess")
              }
              className="mt-1"
              info={trans("Settings.AutoRevokeClientMobileAppAccessInfoText")}
            />
          </Row>
          <Row>
            <ToggleButtonWithInfo
              id="weightProgressInApp"
              label={trans("Settings.WeightProgressInAppLabel")}
              checked={formData?.clientHandling?.weightProgressInApp}
              onChange={(e) => handleToggleChange(e, "weightProgressInApp")}
              className="mt-1"
              info={trans("Settings.WeightProgressInAppInfoLabel")}
            />
          </Row> */}

          {/* <Divider className="mt-3" />
          <Row className="mt-4 d-flex justify-content-between">
            <Col md={8}>
              <h5>{trans("Settings.checkIns")}</h5>
              <p className="fs-6">
                {trans("Settings.allowClientsToFillAndSendCheckIns")}
              </p>
            </Col>
            <Col md={4} className="d-flex justify-content-end">
              <ToggleButtonWithInfo
                id="checkIns"
                label=""
                checked={formData?.checkIns}
                onChange={(e) => handleToggleChange(e, "checkIns")}
                className="mt-1"
              />
            </Col>
          </Row> */}

          {/* {formData.checkIns && ( */}
          <>
            <Divider className="mt-3" />
            <Row className="mt-4 d-flex justify-content-between">
              <Col md={8}>
                <h5>{trans("Settings.sendReminderToClient")}</h5>
              </Col>
            </Row>
            {/* {formData?.checkIns && ( */}
            <>
              <Row className="mt-2">
                <Col md={6} className="h-100">
                  <Select
                    className="w-100 h-100"
                    placeholder="Select an option"
                    onChange={handleSelectChange}
                    value={formData?.sendCheckinReminderEvery?.occur}
                  >
                    {Occur.map((item) => (
                      <Option key={item.value} value={item.value}>
                        {item.label}
                      </Option>
                    ))}
                  </Select>
                </Col>
                {formData?.sendCheckinReminderEvery?.occur === "week" && (
                  <Col md={6}>
                    <Select
                      mode="multiple"
                      placeholder="Select days"
                      onChange={handleWeekDaysChange}
                      // value={formData?.sendCheckinReminderEvery.on.split(
                      //   ","
                      // )}
                      value={
                        formData?.sendCheckinReminderEvery?.on
                          ? formData?.sendCheckinReminderEvery?.on?.split(",")
                          : []
                      }
                      options={weekDays}
                      className="w-100"
                      allowClear
                    ></Select>
                  </Col>
                )}

                {formData?.sendCheckinReminderEvery?.occur === "month" && (
                  <Col md={6}>
                    <div style={{ position: "relative" }}>
                      <div style={{ display: "flex" }}>
                        <input
                          type="text"
                          readOnly
                          value={formatSelectedDays()}
                          onClick={toggleCalendar}
                          style={{
                            padding: "6px",
                            width: "100%",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                        />
                        <FaCalendarAlt
                          onClick={toggleCalendar}
                          style={{
                            position: "absolute",
                            top: "50%",
                            right: "5px",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            fontSize: "18px",
                            color: "#333",
                          }}
                        />
                      </div>

                      {calendarOpen && (
                        <div
                          style={{
                            position: "absolute",
                            zIndex: 1000,
                            backgroundColor: "white",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            marginTop: "5px",
                          }}
                        >
                          <DatePicker
                            selected={null}
                            onChange={handleDateChange}
                            open={true}
                            onClickOutside={() => setCalendarOpen(false)}
                            highlightDates={adjustForInvalidDays(
                              currentMonth
                            )?.map((day) => {
                              // Highlight valid selected days for the current month
                              const date = new Date(currentMonth);
                              date.setDate(day);
                              return date;
                            })}
                            // Highlight the selected days
                            // Don't close calendar immediately after selecting a day
                            shouldCloseOnSelect={false}
                            // Show one month
                            monthsShown={1}
                            calendarClassName="calendar-custom"
                            // Update current month when the user changes month
                            onMonthChange={handleMonthChange}
                            dayClassName={(date) =>
                              adjustForInvalidDays(currentMonth)?.includes(
                                date.getDate()
                              )
                                ? "selected-day"
                                : ""
                            }
                            inline
                          />
                        </div>
                      )}
                    </div>
                  </Col>
                )}
              </Row>

              <Row className="mt-3">
                <Col md={12}>
                  <p className="pb-1">{trans("Settings.remindClientsVia")}</p>
                  <FormCheck
                    type="checkbox"
                    disabled
                    label={trans("Settings.pushNotification")}
                    name="pushNotification"
                    checked={true}
                    onChange={handleReminderMethodChange}
                  />
                  <FormCheck
                    type="checkbox"
                    label={trans("Settings.chatMessage")}
                    name="chatMessages"
                    checked={formData?.remindClientVia?.chatMessages}
                    onChange={handleReminderMethodChange}
                  />
                  <FormCheck
                    type="checkbox"
                    label={trans("Settings.email")}
                    name="email"
                    checked={formData?.remindClientVia?.email}
                    onChange={handleReminderMethodChange}
                  />
                </Col>
              </Row>
            </>
            {/* )} */}
          </>
          {/* )} */}
        </Form>
      )}
      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default Settings;
